import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {PatchTeamPreferencesTeammateError} from '../../../../models/api/patchTeamPreferencesModel';
import {Banner, BannerTypesEnum} from '../../../common/banners/banner';
import {BannerCloseAction} from '../../../common/banners/bannerCloseAction';
import {BannerContent} from '../../../common/banners/bannerContent';
import {BannerContentTitle} from '../../../common/banners/bannerContentTitle';
import {BannerIcon} from '../../../common/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../common/banners/bannersContext';

/*
 * Enums.
 */

enum TeammateErrorCodesEnum {
  NO_PERMISSION = 'no_permission',
  PREFERENCES_ERROR = 'preferences_error',
}

/*
 * Props.
 */

interface PinTagErrorsBannerProps {
  errors: ReadonlyArray<PatchTeamPreferencesTeammateError>;
  onDismissClick: Handler;
}

/*
 * Component.
 */

export const PinTagErrorBanners: FC<PinTagErrorsBannerProps> = (props) => {
  const theme = useUserTheme();
  const {errors, onDismissClick} = props;
  if (errors.length === 0) {
    return null;
  }

  return (
    <Banner
      bannerType={BannerTypesEnum.MULTILINE}
      color={theme.banner.successBackground}
      priority={BannerPrioritiesEnum.TRANSIENT}
    >
      <BannerIcon iconName="checkmarkCircleSmall" />

      <BannerContent>
        <BannerContentTitle>
          <Trans id="banner-tag-pinned-to-workspaces-title">Tag created</Trans>
        </BannerContentTitle>
        {renderBannerMessage(errors)}
      </BannerContent>
      <BannerCloseAction onClick={onDismissClick} />
    </Banner>
  );
};

/*
 * Helpers.
 */

function renderBannerMessage(
  tagPinnedToWorkspacesBannerTeammateErrors: ReadonlyArray<PatchTeamPreferencesTeammateError>,
) {
  const hasNoPermissionTeammate = tagPinnedToWorkspacesBannerTeammateErrors.some(
    (teammateError) => teammateError.errorCode === TeammateErrorCodesEnum.NO_PERMISSION,
  );

  if (hasNoPermissionTeammate) {
    return (
      <Trans id="banner-tag-pinned-to-workspaces-no-permission">
        Note: This tag was not pinned for some users since you do not have permission to manage them.
      </Trans>
    );
  }

  const hasPreferencesErrorTeammate = tagPinnedToWorkspacesBannerTeammateErrors.some(
    (teammateError) => teammateError.errorCode === TeammateErrorCodesEnum.PREFERENCES_ERROR,
  );

  if (hasPreferencesErrorTeammate) {
    return (
      <Trans id="banner-tag-pinned-to-workspaces-preferences-error">
        Note: This tag was not pinned for some users. Check that these teammates have less than 500 tags
        pinned, and try deleting and creating the tag again.
      </Trans>
    );
  }

  return (
    <Trans id="banner-tag-pinned-to-workspaces-catchall">
      This tag was not pinned for some users. Try deleting and creating the tag again, or contact
      support@frontapp.com.
    </Trans>
  );
}
